import React, { useEffect, useState } from "react";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { db } from "../../firebase.config";
import { collection, getDocs, query, where } from "firebase/firestore";
import { Link, useNavigate } from "react-router-dom";
import ScaleSpinner from "../feedback/ScaleSpinner";
import { FaHandHoldingUsd, FaBed, FaBath, FaRulerCombined, FaMapMarkerAlt } from "react-icons/fa";

const responsive = {
  superLargeDesktop: {
    breakpoint: { max: 4000, min: 3000 },
    items: 5,
  },
  desktop: {
    breakpoint: { max: 3000, min: 1280 },
    items: 5,
  },
  desktopMedium: {
    breakpoint: { max: 1280, min: 1024 },
    items: 4,
  },
  tablet: {
    breakpoint: { max: 1024, min: 768 },
    items: 3,
  },
  mobile: {
    breakpoint: { max: 768, min: 0 },
    items: 1,
  },
};

// Pakistani number formatting function
const formatPakistaniCurrency = (amount) => {
  if (!amount || amount === 0) return "Price on request";
  
  const num = Number(amount);
  
  if (num < 100000) {
    return `PKR ${num.toLocaleString()}`;
  }
  
  if (num >= 100000 && num < 10000000) {
    const lacs = num / 100000;
    if (Number.isInteger(lacs)) {
      return `PKR ${lacs} Lac${lacs > 1 ? 's' : ''}`;
    } else {
      return `PKR ${lacs.toFixed(1)} Lac${lacs > 1 ? 's' : ''}`;
    }
  }
  
  if (num >= 10000000 && num < 1000000000) {
    const crores = num / 10000000;
    if (Number.isInteger(crores)) {
      return `PKR ${crores} Crore${crores > 1 ? 's' : ''}`;
    } else {
      return `PKR ${crores.toFixed(1)} Crore${crores > 1 ? 's' : ''}`;
    }
  }
  
  if (num >= 1000000000) {
    const arab = num / 1000000000;
    if (Number.isInteger(arab)) {
      return `PKR ${arab} Arab${arab > 1 ? 's' : ''}`;
    } else {
      return `PKR ${arab.toFixed(1)} Arab${arab > 1 ? 's' : ''}`;
    }
  }
  
  return `PKR ${num.toLocaleString()}`;
};

// Helper function to check if property should show bedrooms and bathrooms
const shouldShowBedsAndBaths = (propertyType) => {
  if (!propertyType) return false;
  
  const type = propertyType.toLowerCase();
  const residentialTypes = [
    'house', 'apartment', 'flat', 'villa', 'townhouse', 
    'banglow', 'cottage', 'condo', 'duplex', 'penthouse',
    'residential', 'home', 'mansion', 'farmhouse',
    'lower portion', 'upper portion', 'full house'
  ];
  
  const landTypes = ['plot', 'land', 'agricultural land', 'commercial plot', 'residential plot'];
  
  if (landTypes.some(landType => type.includes(landType))) {
    return false;
  }
  
  return residentialTypes.some(resType => type.includes(resType));
};

const FeaturedProperties = () => {
  const [saleProperties, setSaleProperties] = useState([]);
  const [rentProperties, setRentProperties] = useState([]);
  const [loading, setLoading] = useState({ sale: true, rent: true });
  const [error, setError] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const [saleSnap, rentSnap] = await Promise.all([
          getDocs(query(collection(db, "properties"), where("featured", "==", true), where("type", "==", "sale"))),
          getDocs(query(collection(db, "properties"), where("featured", "==", true), where("type", "==", "rent")))
        ]);

        const saleList = saleSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const rentList = rentSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const sortProperties = (props) => {
          return props.sort((a, b) => {
            if (a.ranking && b.ranking) return a.ranking - b.ranking;
            const aTime = a.timestamp?.toMillis?.() || a.createdAt?.toMillis?.() || 0;
            const bTime = b.timestamp?.toMillis?.() || b.createdAt?.toMillis?.() || 0;
            return bTime - aTime;
          });
        };

        setSaleProperties(sortProperties(saleList));
        setRentProperties(sortProperties(rentList));
        setLoading({ sale: false, rent: false });
      } catch (err) {
        console.error("Error fetching property listings:", err);
        setError("Failed to fetch property listings.");
        setLoading({ sale: false, rent: false });
      }
    };

    fetchProperties();
  }, []);

  const checkDetails = (id) => {
    navigate(`/category/property/${id}`);
  };

  const getPropertyImage = (property) => {
    return property.propertyImages?.[0] || 
           property.images?.[0] || 
           property.thumbnail || 
           "https://via.placeholder.com/400x300?text=No+Image";
  };

  const getPropertyTitle = (property) => {
    return property.title || 
           property.propertyTitle || 
           `${property.propertyType || 'Property'}`;
  };

  const getPropertyLocation = (property) => {
    return property.location || property.city || "Location not specified";
  };

  const getPropertyPrice = (property) => {
    if (!property.price) return "Price on request";
    const displayPrice = property.offer && property.discountedPrice ? property.discountedPrice : property.price;
    return formatPakistaniCurrency(displayPrice);
  };

  const getOriginalPrice = (property) => {
    if (property.offer && property.price) {
      return formatPakistaniCurrency(property.price);
    }
    return null;
  };

  const formatCurrency = (amount) => {
    if (!amount) return null;
    return formatPakistaniCurrency(amount);
  };

  const renderCarousel = (properties, isLoading, type) => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-40">
          <ScaleSpinner size={8} color="#10b981" />
        </div>
      );
    }

    if (properties.length === 0) {
      return (
        <div className="text-center py-6">
          <div className="bg-gray-50 rounded-lg p-4 max-w-md mx-auto shadow-sm border border-gray-200">
            <svg
              className="w-10 h-10 mx-auto text-gray-400 mb-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            <p className="text-gray-500 text-xs">
              No featured properties available
            </p>
          </div>
        </div>
      );
    }

    return (
      <Carousel
        responsive={responsive}
        swipeable={true}
        draggable={true}
        showDots={true}
        infinite={properties.length > 4}
        autoPlay={properties.length > 1}
        autoPlaySpeed={5000}
        keyBoardControl={true}
        customTransition="all .5s ease-in-out"
        transitionDuration={500}
        containerClass="carousel-container pb-8"
        removeArrowOnDeviceType={["tablet", "mobile"]}
        dotListClass="custom-dot-list-style"
        itemClass="px-1.5"
      >
        {properties.map((property) => {
          const showBedsBaths = shouldShowBedsAndBaths(property.propertyType);
          
          return (
            <div
              key={property.id}
              className="bg-white rounded-lg overflow-hidden hover:shadow-xl transition-all duration-300 h-full flex flex-col border border-gray-200 group cursor-pointer"
              onClick={() => checkDetails(property.id)}
            >
              {/* Image Section */}
              <div className="relative h-36 w-full overflow-hidden bg-gray-100">
                <img
                  src={getPropertyImage(property)}
                  alt={getPropertyTitle(property)}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => {
                    e.target.src = "https://via.placeholder.com/400x300?text=No+Image";
                  }}
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                {/* Hover Overlay Text */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-black/50 backdrop-blur-sm">
                  <span className="bg-emerald-600 text-white text-xs px-3 py-1.5 rounded-full font-semibold shadow-lg transform group-hover:scale-105 transition-transform duration-300">
                    View Details
                  </span>
                </div>
                
                {/* Property Type Badge */}
                {property.propertyType && (
                  <div className="absolute top-1.5 left-1.5">
                    <span className="bg-emerald-600 text-white text-[10px] px-1.5 py-0.5 rounded-full capitalize shadow-md">
                      {property.propertyType.replace(/-/g, ' ').substring(0, 12)}
                    </span>
                  </div>
                )}
                
                {/* Badges Container */}
                <div className="absolute top-1.5 right-1.5 flex gap-1">
                  {property.featured && (
                    <span className="bg-emerald-600 text-white text-[10px] px-1.5 py-0.5 rounded-full shadow-md">
                      Featured
                    </span>
                  )}
                  
                  {property.installmentAvailable && (
                    <span className="bg-emerald-600 text-white text-[10px] px-1.5 py-0.5 rounded-full shadow-md flex items-center gap-0.5">
                      <FaHandHoldingUsd size={8} />
                      <span>Inst</span>
                    </span>
                  )}
                  
                  {property.offer && (
                    <span className="bg-orange-500 text-white text-[10px] px-1.5 py-0.5 rounded-full shadow-md">
                      Offer
                    </span>
                  )}
                </div>
              </div>
              
              {/* Content Section */}
              <div className="p-2.5 flex-grow">
                <h3 className="text-sm font-bold text-gray-800 mb-1 line-clamp-1 group-hover:text-emerald-600 transition-colors">
                  {getPropertyTitle(property)}
                </h3>
                
                {/* Location */}
                <div className="flex items-center mb-1.5">
                  <FaMapMarkerAlt className="w-2.5 h-2.5 text-emerald-500 mr-0.5" />
                  <span className="text-gray-600 text-[10px] line-clamp-1">{getPropertyLocation(property)}</span>
                </div>

                {/* Property Features */}
                {(showBedsBaths || property.area || property.marla) && (
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {showBedsBaths && property.bedrooms > 0 && (
                      <div className="flex items-center gap-0.5 text-gray-600" title={`${property.bedrooms} Bed`}>
                        <FaBed size={10} className="text-emerald-500" />
                        <span className="text-[10px] font-medium">{property.bedrooms}</span>
                      </div>
                    )}
                    {showBedsBaths && property.bathrooms > 0 && (
                      <div className="flex items-center gap-0.5 text-gray-600" title={`${property.bathrooms} Bath`}>
                        <FaBath size={10} className="text-emerald-500" />
                        <span className="text-[10px] font-medium">{property.bathrooms}</span>
                      </div>
                    )}
                    {property.area > 0 && (
                      <div className="flex items-center gap-0.5 text-gray-600" title={`${property.area.toLocaleString()} sq ft`}>
                        <FaRulerCombined size={10} className="text-emerald-500" />
                        <span className="text-[10px] font-medium">{property.area.toLocaleString()}</span>
                      </div>
                    )}
                    {property.marla > 0 && (
                      <div className="flex items-center gap-0.5 text-gray-600" title={`${property.marla} Marla`}>
                        <FaRulerCombined size={10} className="text-emerald-500" />
                        <span className="text-[10px] font-medium">{property.marla}M</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Price Section */}
                <div className="mb-2">
                  <div className="flex items-baseline gap-1 flex-wrap">
                    <p className="text-sm font-bold text-emerald-600">
                      {getPropertyPrice(property)}
                      {property.type === "rent" && (
                        <span className="text-[9px] font-normal text-gray-500 ml-0.5">/mo</span>
                      )}
                    </p>
                    {getOriginalPrice(property) && (
                      <p className="text-[9px] line-through text-gray-400">
                        {getOriginalPrice(property)}
                      </p>
                    )}
                  </div>
                </div>

                {/* Installment Preview */}
                {property.installmentAvailable && (
                  <div className="mb-2 p-1.5 bg-emerald-50 rounded border border-emerald-200">
                    <div className="flex items-center justify-between mb-0.5">
                      <div className="flex items-center gap-0.5">
                        <FaHandHoldingUsd size={8} className="text-emerald-600" />
                        <span className="text-[9px] font-semibold text-emerald-700">Installments</span>
                      </div>
                      <span className="text-[8px] text-emerald-600">0% Interest</span>
                    </div>
                    <div className="grid grid-cols-3 gap-0.5 text-center">
                      {property.downPayment > 0 && (
                        <div>
                          <p className="text-[8px] text-gray-500">Down</p>
                          <p className="text-[9px] font-bold text-emerald-700">
                            {formatCurrency(property.downPayment)}
                          </p>
                        </div>
                      )}
                      {property.monthlyPayment > 0 && (
                        <div>
                          <p className="text-[8px] text-gray-500">Monthly</p>
                          <p className="text-[9px] font-bold text-emerald-700">
                            {formatCurrency(property.monthlyPayment)}
                          </p>
                        </div>
                      )}
                      {property.installmentDuration > 0 && (
                        <div>
                          <p className="text-[8px] text-gray-500">Duration</p>
                          <p className="text-[9px] font-bold text-emerald-700">
                            {property.installmentDuration}m
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Button */}
              <div className="px-2.5 pb-2.5">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    checkDetails(property.id);
                  }}
                  className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg shadow-md transition-all duration-300 hover:scale-[1.02] active:scale-95"
                >
                  View Details
                </button>
              </div>
            </div>
          );
        })}
      </Carousel>
    );
  };

  if (error) {
    return (
      <section className="py-6 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center py-4">
            <p className="text-red-500 text-xs bg-red-50 inline-block px-3 py-1.5 rounded-lg">
              {error}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-6 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-4">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-1">
            Featured Properties
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-xs">
            Discover our premium selection of high-quality properties
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-200">
          {/* Sale Properties Section */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-bold text-gray-800 border-l-3 border-emerald-500 pl-2">
                For Sale
              </h3>
              {saleProperties.length > 0 && (
                <Link to="/category/sale">
                  <button className="text-emerald-600 hover:text-emerald-700 font-medium text-[10px] transition-colors">
                    View All →
                  </button>
                </Link>
              )}
            </div>
            {renderCarousel(saleProperties, loading.sale, 'sale')}
          </div>

          {/* Rent Properties Section */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-bold text-gray-800 border-l-3 border-emerald-500 pl-2">
                For Rent
              </h3>
              {rentProperties.length > 0 && (
                <Link to="/category/rent">
                  <button className="text-emerald-600 hover:text-emerald-700 font-medium text-[10px] transition-colors">
                    View All →
                  </button>
                </Link>
              )}
            </div>
            {renderCarousel(rentProperties, loading.rent, 'rent')}
          </div>
        </div>

        <div className="text-center mt-5">
          <Link to="/featured-properties">
            <button className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2 px-5 rounded-full shadow-md transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-lg inline-flex items-center text-xs">
              Browse All Featured Properties
              <svg
                className="w-3.5 h-3.5 ml-1.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </button>
          </Link>
        </div>
      </div>

      <style jsx>{`
        :global(.custom-dot-list-style) {
          position: absolute;
          bottom: -20px;
          left: 0;
          right: 0;
          display: flex;
          justify-content: center;
          gap: 6px;
        }
        
        :global(.custom-dot-list-style button) {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background-color: #d1d5db;
          transition: all 0.3s;
          padding: 0;
        }
        
        :global(.custom-dot-list-style .react-multi-carousel-dot--active button) {
          background-color: #10b981;
          width: 18px;
          border-radius: 3px;
        }
        
        :global(.react-multi-carousel-list) {
          padding-bottom: 5px;
        }
        
        :global(.react-multi-carousel-track) {
          padding: 4px 0;
        }
        
        :global(.react-multi-carousel-arrow) {
          background-color: #10b981;
          border-radius: 50%;
          width: 26px;
          height: 26px;
          min-width: 26px;
        }
        
        :global(.react-multi-carousel-arrow:hover) {
          background-color: #059669;
        }
        
        :global(.react-multi-carousel-arrow::before) {
          font-size: 12px;
          color: white;
        }
        
        :global(.react-multi-carousel-arrow--left) {
          left: calc(1% + 1px);
        }
        
        :global(.react-multi-carousel-arrow--right) {
          right: calc(1% + 1px);
        }
        
        .line-clamp-1 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 1;
        }
      `}</style>
    </section>
  );
};

export default FeaturedProperties;